import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { CommonDirection } from 'shared/entities/common/client';

import {
  getHorizontalScrollBackStep,
  getHorizontalScrollForwardTarget
} from '../utils/getHorizontalScrollStepFromVisibleItem';
import {
  canScrollHorizontallyLeft,
  canScrollHorizontallyRight
} from '../utils/horizontalScrollEdge';
import {
  resolveScrollElement,
  ScrollContainerTarget
} from '../utils/scrollContainerTarget';

import type { ScrollController } from './horizontalScroll/types';

type HorizontalScrollDirection = CommonDirection.left | CommonDirection.right;

export type UseHorizontalScrollArrowsOptions = {
  enabled: boolean;
  target: ScrollContainerTarget;
  itemsTrackRef?: ScrollContainerTarget;
  updateOnResize: boolean;
  autoScrollStep: boolean;
  scrollStep?: number;
  controllerRef: React.MutableRefObject<ScrollController | null>;
};

const useHorizontalScrollArrows = ({
  enabled,
  target,
  itemsTrackRef,
  updateOnResize,
  autoScrollStep,
  scrollStep,
  controllerRef
}: UseHorizontalScrollArrowsOptions) => {
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const updateScrollButtons = React.useCallback(() => {
    const element = resolveScrollElement(target);
    if (!element) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = element;
    const nextLeft = canScrollHorizontallyLeft(scrollLeft);
    const nextRight = canScrollHorizontallyRight(
      scrollLeft,
      scrollWidth,
      clientWidth
    );
    setCanScrollLeft((prev) => (prev === nextLeft ? prev : nextLeft));
    setCanScrollRight((prev) => (prev === nextRight ? prev : nextRight));
  }, [target]);

  React.useEffect(() => {
    if (!enabled || !updateOnResize) {
      return;
    }

    const container = resolveScrollElement(target);
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(container);

    const track = resolveScrollElement(itemsTrackRef ?? null);
    if (track) {
      observer.observe(track);
    }

    return () => {
      observer.disconnect();
    };
  }, [enabled, updateOnResize, target, itemsTrackRef, updateScrollButtons]);

  const performArrowScroll = React.useCallback(
    (direction: HorizontalScrollDirection) => {
      const element = resolveScrollElement(target);
      const controller = controllerRef.current;
      if (!element || !controller) {
        return;
      }

      if (autoScrollStep) {
        const track = resolveScrollElement(itemsTrackRef ?? null);
        if (!track) {
          return;
        }

        if (direction === CommonDirection.right) {
          controller.scrollTo(getHorizontalScrollForwardTarget(element, track));
        } else {
          controller.scrollBy(-getHorizontalScrollBackStep(element, track));
        }
        return;
      }

      if (scrollStep !== undefined) {
        const delta =
          direction === CommonDirection.right ? scrollStep : -scrollStep;
        controller.scrollBy(delta);
      }
    },
    [autoScrollStep, controllerRef, itemsTrackRef, scrollStep, target]
  );

  const handleRight = React.useCallback(
    () => performArrowScroll(CommonDirection.right),
    [performArrowScroll]
  );

  const handleLeft = React.useCallback(
    () => performArrowScroll(CommonDirection.left),
    [performArrowScroll]
  );

  return {
    canScrollLeft,
    canScrollRight,
    handleLeft,
    handleRight,
    updateScrollButtons
  };
};

export default useHorizontalScrollArrows;
